import { memo } from 'react';

// MUI imports
import CloseIcon from '@mui/icons-material/Close';
import { Box, Modal, Typography, Button } from '@mui/material';

// Constants
import {
  BUTTON_COLORS,
  BUTTON_SIZE,
  BUTTON_VARIANT_THEMES,
} from 'utils/constants';

import { deleteConfirmStyle } from './styles';

interface DeleteFilesConfirmModalProps {
  open: boolean;
  fileNames: string[];
  folderName: string;
  isDeleting?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirmation shown before any file deletion - single row or bulk.
 *
 * Deletion removes the file from storage as well as the joblog and cannot be reversed,
 * so the files are named explicitly rather than described as a count: an admin about to
 * destroy the wrong document should be able to see that from this dialog alone.
 *
 * The X and Cancel are wired to the same handler, so closing the dialog can never be
 * mistaken for confirming it.
 */
const DeleteFilesConfirmModal = ({
  open,
  fileNames,
  folderName,
  isDeleting = false,
  onConfirm,
  onCancel,
}: DeleteFilesConfirmModalProps) => {
  const isSingle = fileNames.length === 1;

  return (
    <Modal
      open={open}
      onClose={onCancel}
      aria-labelledby="delete-files-confirm-title"
      aria-describedby="delete-files-confirm-description"
    >
      <Box sx={deleteConfirmStyle}>
        <div className="d-flex justify-space-between align-start">
          <Typography
            id="delete-files-confirm-title"
            variant="h3"
            component="h3"
            className="my-12"
          >
            Delete {isSingle ? 'file' : `${fileNames.length} files`}
          </Typography>
          <CloseIcon
            className="mt-8 cursor-pointer"
            aria-label="close"
            onClick={onCancel}
          />
        </div>

        <Typography
          id="delete-files-confirm-description"
          className="f-16 l-h-22 text-black-7 mt-8"
        >
          You are attempting to permanently delete the following{' '}
          {isSingle ? 'file' : 'files'} from the {folderName} folder:
        </Typography>

        <ul className="mt-8 mb-8 delete-file-list">
          {fileNames.map(name => (
            <li key={name} className="f-16 f-w-600 l-h-22 text-black-7">
              {name}
            </li>
          ))}
        </ul>

        <Typography className="f-16 f-w-600 l-h-22 text-error">
          This cannot be undone.
        </Typography>

        <div className="d-flex justify-end mt-24">
          <Button
            variant={BUTTON_VARIANT_THEMES.outlined}
            size={BUTTON_SIZE.medium}
            color={BUTTON_COLORS.primary}
            onClick={onCancel}
            className="mr-8"
          >
            CANCEL
          </Button>
          <Button
            variant={BUTTON_VARIANT_THEMES.contained}
            size={BUTTON_SIZE.medium}
            color={BUTTON_COLORS.error}
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? 'DELETING…' : 'CONFIRM'}
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export default memo(DeleteFilesConfirmModal);
